import { featureTemplateService } from "@/services/featureTemplateService";
import { FeatureTemplate } from "@/types/featureTemplate";
import { format, isAfter, isBefore } from "date-fns";
import {
  CalendarIcon,
  Clock,
  Filter,
  Plus,
  Search,
  SortAsc,
  SortDesc,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useOrganization } from "../../App";
import { campaignService } from "../../services/campaignService";
import { Campaign } from "../../types/campaign";
import CreateCampaignForm from "../campaigns/CreateCampaignForm";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { LoadingScreen } from "../ui/loading-spinner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/separator";

type SortField = "name" | "created_at" | "updated_at";
type SortDirection = "asc" | "desc";

interface Filters {
  search: string;
  name: string;
  createdAfter: Date | null;
  createdBefore: Date | null;
  updatedAfter: Date | null;
  updatedBefore: Date | null;
  sortField: SortField;
  sortDirection: SortDirection;
}

export default function UseCaseTemplatePage() {
  const { templateName } = useParams<{ templateName: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { currentOrganization } = useOrganization();
  const [featureTemplate, setFeatureTemplate] =
    useState<FeatureTemplate | null>(null);
  const navigate = useNavigate();

  // Initialize filters from URL params
  const [filters, setFilters] = useState<Filters>({
    search: searchParams.get("search") || "",
    name: searchParams.get("name") || "",
    createdAfter: searchParams.get("createdAfter")
      ? new Date(searchParams.get("createdAfter")!)
      : null,
    createdBefore: searchParams.get("createdBefore")
      ? new Date(searchParams.get("createdBefore")!)
      : null,
    updatedAfter: searchParams.get("updatedAfter")
      ? new Date(searchParams.get("updatedAfter")!)
      : null,
    updatedBefore: searchParams.get("updatedBefore")
      ? new Date(searchParams.get("updatedBefore")!)
      : null,
    sortField: (searchParams.get("sortField") as SortField) || "created_at",
    sortDirection:
      (searchParams.get("sortDirection") as SortDirection) || "desc",
  });

  // Filter sidebar state
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(true);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.search) params.set("search", filters.search);
    if (filters.name) params.set("name", filters.name);
    if (filters.createdAfter)
      params.set("createdAfter", filters.createdAfter.toISOString());
    if (filters.createdBefore)
      params.set("createdBefore", filters.createdBefore.toISOString());
    if (filters.updatedAfter)
      params.set("updatedAfter", filters.updatedAfter.toISOString());
    if (filters.updatedBefore)
      params.set("updatedBefore", filters.updatedBefore.toISOString());
    params.set("sortField", filters.sortField);
    params.set("sortDirection", filters.sortDirection);

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Apply filters to campaigns
  useEffect(() => {
    if (!campaigns.length) {
      setFilteredCampaigns([]);
      return;
    }

    let result = [...campaigns];

    // Apply text search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (campaign) =>
          campaign.name.toLowerCase().includes(searchLower) ||
          (campaign.description &&
            campaign.description.toLowerCase().includes(searchLower))
      );
    }

    // Apply name filter
    if (filters.name) {
      const nameLower = filters.name.toLowerCase();
      result = result.filter((campaign) =>
        campaign.name.toLowerCase().includes(nameLower)
      );
    }

    // Apply date filters
    if (filters.createdAfter) {
      result = result.filter((campaign) =>
        isAfter(new Date(campaign.created_at), filters.createdAfter!)
      );
    }

    if (filters.createdBefore) {
      result = result.filter((campaign) =>
        isBefore(new Date(campaign.created_at), filters.createdBefore!)
      );
    }

    if (filters.updatedAfter) {
      result = result.filter((campaign) =>
        isAfter(new Date(campaign.updated_at), filters.updatedAfter!)
      );
    }

    if (filters.updatedBefore) {
      result = result.filter((campaign) =>
        isBefore(new Date(campaign.updated_at), filters.updatedBefore!)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      if (filters.sortField === "name") {
        comparison = a.name.localeCompare(b.name);
      } else if (filters.sortField === "created_at") {
        comparison =
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (filters.sortField === "updated_at") {
        comparison =
          new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      }

      return filters.sortDirection === "asc" ? comparison : -comparison;
    });

    setFilteredCampaigns(result);
  }, [campaigns, filters]);

  useEffect(() => {
    const fetchData = async () => {
      if (!templateName || !currentOrganization) return;

      setLoading(true);
      try {
        // First get the feature template
        const templates = await featureTemplateService.getFeatureTemplates(
          currentOrganization.id
        );
        const template = templates.find((t) => t.name === templateName);
        if (!template) throw new Error("Feature template not found");
        setFeatureTemplate(template);

        // Then get campaigns for this template
        const { data, error } = await campaignService.getCampaigns(
          currentOrganization.id
        );
        if (error) throw error;
        if (data) {
          // Filter campaigns by feature_template_id
          const filteredCampaigns = data.filter(
            (campaign) => campaign.feature_template_id === template.id
          );
          setCampaigns(filteredCampaigns);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [templateName, currentOrganization]);

  const handleCreateNew = () => {
    setIsCreateDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsCreateDialogOpen(false);
  };

  const handleCreateSuccess = (newCampaign: Campaign) => {
    setCampaigns((prev) => [newCampaign, ...prev]);
    setIsCreateDialogOpen(false);
  };

  const handleRowClick = (campaignId: string) => {
    navigate(`/u/${templateName}/${campaignId}`);
  };

  const updateFilter = (key: keyof Filters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      name: "",
      createdAfter: null,
      createdBefore: null,
      updatedAfter: null,
      updatedBefore: null,
      sortField: "created_at",
      sortDirection: "desc",
    });
  };

  const toggleFilterSidebar = () => {
    setIsFilterSidebarOpen(!isFilterSidebarOpen);
  };

  // Count active filters
  const activeFilterCount = [
    filters.search,
    filters.name,
    filters.createdAfter,
    filters.createdBefore,
    filters.updatedAfter,
    filters.updatedBefore,
  ].filter(Boolean).length;

  // Format date for display
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return format(date, "MMM d, yyyy");
  };

  if (loading) {
    return <LoadingScreen text="Loading campaigns..." />;
  }

  if (!templateName || !currentOrganization || !featureTemplate) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          Missing required information to display campaigns.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">{templateName}</h1>
          <p className="text-muted-foreground">
            Manage your {templateName.toLowerCase()} campaigns
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFilterSidebar}
            className="flex items-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <Button onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create {templateName}
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Filter Sidebar */}
        {isFilterSidebarOpen && (
          <div className="w-64 shrink-0">
            <div className="bg-card border p-4 sticky top-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">Filters</h3>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="h-8 px-2 text-xs"
                  >
                    Reset
                  </Button>
                )}
              </div>

              <ScrollArea className="pr-4 max-h-[calc(100vh-200px)]">
                <div className="space-y-4">
                  {/* Search */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Search
                    </label>
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search all fields"
                        value={filters.search}
                        onChange={(e) => updateFilter("search", e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Name filter */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Name
                    </label>
                    <Input
                      placeholder="Filter by name"
                      value={filters.name}
                      onChange={(e) => updateFilter("name", e.target.value)}
                    />
                  </div>

                  <Separator />

                  {/* Created date range */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Created Date
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start text-left font-normal h-9"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {filters.createdAfter
                              ? formatDate(filters.createdAfter)
                              : "From"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={filters.createdAfter || undefined}
                            onSelect={(date) =>
                              updateFilter("createdAfter", date)
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start text-left font-normal h-9"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {filters.createdBefore
                              ? formatDate(filters.createdBefore)
                              : "To"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={filters.createdBefore || undefined}
                            onSelect={(date) =>
                              updateFilter("createdBefore", date)
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Clear created date filters */}
                    {(filters.createdAfter || filters.createdBefore) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          updateFilter("createdAfter", null);
                          updateFilter("createdBefore", null);
                        }}
                        className="mt-2 h-8 text-xs w-full"
                      >
                        <X className="h-3 w-3 mr-1" /> Clear dates
                      </Button>
                    )}
                  </div>

                  <Separator />

                  {/* Updated date range */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Updated Date
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start text-left font-normal h-9"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {filters.updatedAfter
                              ? formatDate(filters.updatedAfter)
                              : "From"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={filters.updatedAfter || undefined}
                            onSelect={(date) =>
                              updateFilter("updatedAfter", date)
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start text-left font-normal h-9"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {filters.updatedBefore
                              ? formatDate(filters.updatedBefore)
                              : "To"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={filters.updatedBefore || undefined}
                            onSelect={(date) =>
                              updateFilter("updatedBefore", date)
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Clear updated date filters */}
                    {(filters.updatedAfter || filters.updatedBefore) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          updateFilter("updatedAfter", null);
                          updateFilter("updatedBefore", null);
                        }}
                        className="mt-2 h-8 text-xs w-full"
                      >
                        <X className="h-3 w-3 mr-1" /> Clear dates
                      </Button>
                    )}
                  </div>

                  <Separator />

                  {/* Sorting */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Sort By
                    </label>
                    <div className="flex gap-2">
                      <Select
                        value={filters.sortField}
                        onValueChange={(value) =>
                          updateFilter("sortField", value)
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name">Name</SelectItem>
                          <SelectItem value="created_at">
                            Created Date
                          </SelectItem>
                          <SelectItem value="updated_at">
                            Updated Date
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() =>
                          updateFilter(
                            "sortDirection",
                            filters.sortDirection === "asc" ? "desc" : "asc"
                          )
                        }
                        className="h-10 w-10"
                      >
                        {filters.sortDirection === "asc" ? (
                          <SortAsc className="h-4 w-4" />
                        ) : (
                          <SortDesc className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1">
          {campaigns.length === 0 ? (
            <div className="text-center py-12 bg-muted/50 rounded-lg">
              <h3 className="text-lg font-medium mb-2">No campaigns found</h3>
              <p className="text-muted-foreground">
                Get started by creating your first {templateName.toLowerCase()}{" "}
                campaign
              </p>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-12 bg-muted/50 rounded-lg">
              <h3 className="text-lg font-medium mb-2">
                No matching campaigns
              </h3>
              <p className="text-muted-foreground">
                Try adjusting your filters to find what you're looking for
              </p>
              <Button variant="outline" className="mt-4" onClick={resetFilters}>
                Clear all filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredCampaigns.map((campaign) => (
                <Card
                  key={campaign.id}
                  className="cursor-pointer hover:shadow-md transition-all"
                  onClick={() => handleRowClick(campaign.id)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{campaign.name}</CardTitle>
                      <Badge
                        variant={
                          campaign.status === "active"
                            ? "success"
                            : campaign.status === "paused"
                            ? "warning"
                            : "secondary"
                        }
                      >
                        {campaign.status || "Draft"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {campaign.description && (
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {campaign.description}
                      </p>
                    )}
                    <div className="flex flex-col space-y-2 text-sm">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>0 leads</span>
                      </div>
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>
                          Created{" "}
                          {format(new Date(campaign.created_at), "MMM d, yyyy")}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>
                          Updated{" "}
                          {format(new Date(campaign.updated_at), "MMM d, yyyy")}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create {templateName} Campaign</DialogTitle>
            <DialogDescription>
              Fill in the details below to create a new campaign.
            </DialogDescription>
          </DialogHeader>
          <CreateCampaignForm
            featureTemplateId={featureTemplate.id}
            organizationId={currentOrganization.id}
            onClose={handleDialogClose}
            onSuccess={handleCreateSuccess}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
